package org.example.project.screens_sys

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.coroutines.launch
import kotlinx.browser.window
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.example.project.enginekalender.loadServiceKalenderBookings
import org.example.project.enginekalender.searchServiceKalenderBookings
import org.example.project.engineservice.ServiceApi
import org.example.project.enginetabular.TABULAR_DOCUMENT_ICON_VALUE
import org.example.project.enginetabular.TabularScreen
import org.example.project.screens_cus.CUSCeoDashboardStyles
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.css.gridTemplateColumns
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text

private const val SYS_DASHBOARD_MWST_SATZ = 0.19

@Composable
fun SysSystemDashboard() {
    Style(CUSCeoDashboardStyles)

    var loading by remember { mutableStateOf(true) }
    var kpiKunden by remember { mutableStateOf(0) }
    var kpiNetzwerk by remember { mutableStateOf(0) }
    var kpiTermine by remember { mutableStateOf(0) }
    var kpiSupport by remember { mutableStateOf(0) }
    var kpiCrs by remember { mutableStateOf(0) }
    var selectedPanel by remember { mutableStateOf("") }
    var kunden by remember { mutableStateOf<List<All>>(emptyList()) }
    var netzwerkKunden by remember { mutableStateOf<List<All>>(emptyList()) }
    var termine by remember { mutableStateOf<List<All>>(emptyList()) }
    var support by remember { mutableStateOf<List<All>>(emptyList()) }
    var crs by remember { mutableStateOf<List<All>>(emptyList()) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        val loadedKunden = loadSysDashboardKunden()
        kpiKunden = loadedKunden.size
        kpiNetzwerk = loadSysDashboardNetzwerkFirmen().size
        kpiTermine = runCatching { loadServiceKalenderBookings() }.getOrDefault(emptyList())
            .count { it.kundeFirmenId.isNotBlank() }
        kpiSupport = runCatching { ServiceApi.loadSupport("all") }.getOrDefault(emptyList()).size
        kpiCrs = runCatching { ServiceApi.loadCr("all") }.getOrDefault(emptyList()).size
        loading = false
    }
    val tiles = listOf(
        sysDashboardTile("Firmenregister", "Kunden", "images/kundensetup.svg", kpiKunden.toString()),
        sysDashboardTile("Netzwerkfirmen", "Netzwerkfirmen", "images/networknode.svg", kpiNetzwerk.toString()),
        sysDashboardTile("Terminauskunft", "Termine", "images/kalender.svg", kpiTermine.toString()),
        sysDashboardTile("SupportAuskunftScreen", "Support", "images/supportauskunft.svg", kpiSupport.toString()),
        sysDashboardTile("CRAuskunft", "CR", "images/crinfo.svg", kpiCrs.toString())
    )

    Div({ classes(CUSCeoDashboardStyles.screenRoot) }) {
        Div({ classes(CUSCeoDashboardStyles.stickyTop) }) {
            Div({ classes(CUSCeoDashboardStyles.tileScrollHost) }) {
                Div({
                    classes(CUSCeoDashboardStyles.grid)
                    style {
                        gridTemplateColumns("repeat(5, 240px)")
                        property("width", "max-content")
                        property("margin-left", "auto")
                        property("margin-right", "auto")
                    }
                }) {
                    tiles.forEach { tile ->
                        SysDashboardTile(
                            tile = tile,
                            loading = loading,
                            active = selectedPanel == tile.key,
                            onClick = { selectedPanel = tile.key }
                        )
                    }
                }
            }
        }

        Div({ classes(CUSCeoDashboardStyles.tabularArea) }) {
            if (selectedPanel == "Firmenregister") SysDashboardKundenTable(kunden) { filters ->
                scope.launch { kunden = KundenSetupApi.searchSetup(filters).filter { it.istkunde } }
            }
            if (selectedPanel == "Netzwerkfirmen") SysDashboardKundenTable(netzwerkKunden, "Netzwerkfirmen") { filters ->
                scope.launch { netzwerkKunden = FirmenregisterApi.searchCompanies(filters).filter { it.istnetzwerk } }
            }
            if (selectedPanel == "Terminauskunft") SysDashboardTermineTable(termine, kunden) { filters ->
                scope.launch { termine = searchServiceKalenderBookings(filters).filter { it.kundeFirmenId.isNotBlank() } }
            }
            if (selectedPanel == "SupportAuskunftScreen") SysDashboardSupportTable(support) { filters ->
                scope.launch { support = ServiceApi.searchSupport("all", filters) }
            }
            if (selectedPanel == "CRAuskunft") SysDashboardCrTable(crs) { filters ->
                scope.launch { crs = ServiceApi.searchCr("all", filters) }
            }
        }
    }
}

@Composable
private fun SysDashboardTile(tile: All, loading: Boolean, active: Boolean, onClick: () -> Unit) {
    Div({
        if (active) {
            classes(CUSCeoDashboardStyles.tile, CUSCeoDashboardStyles.tileClickable, CUSCeoDashboardStyles.tileActive)
        } else {
            classes(CUSCeoDashboardStyles.tile, CUSCeoDashboardStyles.tileClickable)
        }
        onClick { onClick() }
    }) {
        Div({ classes(CUSCeoDashboardStyles.iconSlot) }) {
            Img(src = tile.icon, attrs = { classes(CUSCeoDashboardStyles.icon) })
        }
        Div({ classes(CUSCeoDashboardStyles.textColumn) }) {
            Div({ classes(CUSCeoDashboardStyles.tileLabel) }) { Text(tile.label) }
            Div({ classes(CUSCeoDashboardStyles.value) }) {
                Text(if (loading) "..." else tile.bezeichnung)
            }
        }
    }
}

@Composable
private fun SysDashboardKundenTable(rows: List<All>, title: String = "Kunden", onSearch: (Map<String, String>) -> Unit) {
    val headers = listOf("Firmen-ID", "Firma", "PLZ", "Ort", "Netzwerk", "Kunde")
    SysDashboardTabularScreen(
        screenKey = "SYSDashboard_$title",
        title = title,
        headers = headers,
        data = rows.map { row ->
            row.firmenId.ifBlank { row.id } to listOf(
                row.firmenId.ifBlank { row.id },
                row.firma,
                row.plz,
                row.ort,
                sysDashboardCheckboxText(sysDashboardNetzwerkAktiv(row)),
                sysDashboardCheckboxText(row.istkunde)
            )
        },
        onSearchWithFilters = onSearch
    )
}

@Composable
private fun SysDashboardTermineTable(rows: List<All>, kunden: List<All>, onSearch: (Map<String, String>) -> Unit) {
    val firmaByFirmenId = kunden.associate { it.firmenId.ifBlank { it.id } to it.firma }
    val headers = listOf("Datum", "Zeit", "Firma", "Art", "Ort", "Status")
    SysDashboardTabularScreen(
        screenKey = "SYSDashboard_Termine",
        title = "Termine",
        headers = headers,
        data = rows.sortedByDescending { it.datum }.map { termin ->
            val firmenId = termin.kundeFirmenId
            termin.id.ifBlank { "${termin.datum}_${termin.uhrzeitVon}_$firmenId" } to listOf(
                termin.datum,
                listOf(termin.uhrzeitVon, termin.uhrzeitBis).filter { it.isNotBlank() }.joinToString(" - "),
                firmaByFirmenId[firmenId].orEmpty(),
                termin.terminArt.ifBlank { "Termin" },
                termin.ort,
                ZustandManager.Termin.textFor(termin.zustand)
            )
        },
        onSearchWithFilters = onSearch
    )
}

@Composable
private fun SysDashboardSupportTable(rows: List<All>, onSearch: (Map<String, String>) -> Unit) {
    val headers = listOf("Ticketnummer", "Firma", "Screen", "Art", "Mitarbeiter", "Datum", "Zustand", "Ticket")
    SysDashboardTabularScreen(
        screenKey = "SYSDashboard_Support",
        title = "Support",
        headers = headers,
        data = rows.sortedByDescending { it.id.toIntOrNull() ?: 0 }.map { ticket ->
            ticket.id.ifBlank { ticket.ticketnummer.ifBlank { ticket.supportTicket } } to listOf(
                ticket.ticketnummer.ifBlank { ticket.supportTicket.ifBlank { ticket.id } },
                ticket.firma,
                All.names[ticket.screenKey].orEmpty().ifBlank { ticket.screenKey.ifBlank { ticket.ticketgrund } },
                ticket.supportart,
                listOf(ticket.vorname, ticket.nachname).filter { it.isNotBlank() }.joinToString(" ").ifBlank { ticket.personalnummer },
                ticket.bearbeitetAm.ifBlank { ticket.erstelltAm.ifBlank { ticket.datum } },
                ZustandManager.Support.textFor(ticket.zustand),
                TABULAR_DOCUMENT_ICON_VALUE
            )
        },
        onSearchWithFilters = onSearch,
        onCellClick = { rowId, colIndex ->
            if (colIndex == headers.lastIndex) {
                routeToSysDashboardScreen("SupportAuskunftScreen", "ticketId" to rowId)
            }
        }
    )
}

@Composable
private fun SysDashboardCrTable(rows: List<All>, onSearch: (Map<String, String>) -> Unit) {
    val headers = listOf("CR ID", "Firma", "CR Nummer", "Art", "Betreff", "Aufwand geschätzt", "Aufwand final", "Zustand", "CR")
    SysDashboardTabularScreen(
        screenKey = "SYSDashboard_CR",
        title = "CR",
        headers = headers,
        data = rows.sortedByDescending { it.id.toIntOrNull() ?: 0 }.map { cr ->
            cr.id.ifBlank { cr.crNummer } to listOf(
                cr.id,
                cr.kunde.ifBlank { cr.firma },
                cr.crNummer.ifBlank { "#${cr.id}" },
                sysDashboardCrArtLabel(cr),
                cr.crBeschreibung,
                cr.crAufwandStunden.takeIf { it > 0 }?.let { "$it Stunden" }.orEmpty(),
                cr.crAufwandFinal.takeIf { it > 0 }?.let { "$it Stunden" }.orEmpty(),
                ZustandManager.CR.textFor(cr.zustand),
                TABULAR_DOCUMENT_ICON_VALUE
            )
        },
        onSearchWithFilters = onSearch,
        onCellClick = { rowId, colIndex ->
            if (colIndex == headers.lastIndex) {
                routeToSysDashboardScreen("CRAuskunft", "crId" to rowId)
            }
        }
    )
}

@Composable
internal fun SysDashboardEinnahmenTable(
    rows: List<All>,
    onSearch: (Map<String, String>) -> Unit,
    onInvoiceClick: ((All) -> Unit)? = null
) {
    val scope = rememberCoroutineScope()
    val headers = listOf("Firma", "Monat", "Gesamt Netto", "MwSt. 19 %", "Gesamt Brutto", "Rechnung")
    SysDashboardTabularScreen(
        screenKey = "SYSDashboard_Einnahmen",
        title = "Einnahmen",
        headers = headers,
        data = rows.sortedBy { it.firma.lowercase() }.map { row ->
            row.id.ifBlank { "${row.firmenId}_${row.datum}" } to listOf(
                row.firma,
                row.datum,
                sysDashboardEuroText(sysDashboardNetto(row)),
                sysDashboardEuroText(sysDashboardMwst(row)),
                sysDashboardEuroText(sysDashboardBrutto(row)),
                TABULAR_DOCUMENT_ICON_VALUE
            )
        },
        onSearchWithFilters = onSearch,
        onCellClick = { rowId, colIndex ->
            if (colIndex == headers.lastIndex) {
                val row = rows.firstOrNull { it.id.ifBlank { "${it.firmenId}_${it.datum}" } == rowId }
                if (row != null) {
                    if (onInvoiceClick != null) onInvoiceClick(row)
                    else scope.launch {
                        ServiceApi.openServiceDocument("rechnung", rowId, "${rowId}_rechnung.pdf", logMissing = true)
                    }
                }
            }
        }
    )
}

@Composable
private fun SysDashboardTabularScreen(
    screenKey: String,
    title: String,
    headers: List<String>,
    data: List<Pair<String, List<String>>>,
    onSearchWithFilters: ((Map<String, String>) -> Unit)? = null,
    onCellClick: ((String, Int) -> Unit)? = null
) {
    Div({ classes(CUSCeoDashboardStyles.verbrauchTable) }) {
        TabularScreen(
            screenKey = screenKey,
            rollenKey = "",
            title = title,
            headers = headers,
            data = data,
            editable = false,
            hardReadOnly = true,
            includeMetadata = false,
            nonEditableCols = headers.toSet(),
            allowCreate = false,
            allowDelete = false,
            allowSave = false,
            allowImportButton = false,
            allowExportButton = true,
            allowRefreshButton = true,
            allowSearchButton = true,
            allowSettingsButton = true,
            useUserSettings = false,
            smartMode = true,
            onSearchWithFilters = onSearchWithFilters,
            onCellClick = onCellClick,
            onCommit = {}
        )
    }
}

private fun routeToSysDashboardScreen(screenKey: String, vararg params: Pair<String, String>) {
    val baseHash = window.location.hash.substringBefore("?").ifBlank { "#/dashboard" }
    fun encode(value: String): String = js("encodeURIComponent")(value) as String
    val query = buildList {
        add("screen=${encode(screenKey)}")
        params.forEach { (key, value) ->
            if (value.isNotBlank()) add("${encode(key)}=${encode(value)}")
        }
    }.joinToString("&")
    window.location.hash = "$baseHash?$query"
}

private suspend fun loadSysDashboardKunden(): List<All> {
    val detailsByFirmenId = KundenverbrauchApi.loadAllDetails()
        .associateBy { it.firmenId.ifBlank { it.id } }

    return KundenverbrauchApi.loadCompanies()
        .filter { it.istkunde }
        .sortedBy { it.firma.lowercase() }
        .map { firma ->
            val firmenId = firma.firmenId.ifBlank { firma.id }
            val settings = detailsByFirmenId[firmenId]
            firma.copy(
                id = firmenId,
                firmenId = firmenId,
                lizenzkey = settings?.lizenzkey.orEmpty(),
                nodePort = settings?.nodePort.orEmpty(),
                rechnungsdatum = settings?.rechnungsdatum.orEmpty().ifBlank { "1" },
                zustand = if (settings?.gesperrt == true) ZustandManager.Kunde.GESPERRT else firma.zustand
            )
        }
}

private suspend fun loadSysDashboardNetzwerkFirmen(): List<All> =
    FirmenregisterApi.loadCompanies()
        .filter { it.istnetzwerk }
        .sortedBy { it.firma.lowercase() }
        .map { firma ->
            val firmenId = firma.firmenId.ifBlank { firma.id }
            firma.copy(id = firmenId, firmenId = firmenId)
        }

private suspend fun loadSysDashboardInvoices(kunden: List<All>, monthKey: String): List<All> =
    kunden.map { kunde ->
        val loaded = runCatching { KundenverbrauchApi.loadVerbrauch(kunde.firmenId, monthKey) }.getOrNull()
        (loaded ?: All(
            id = "rechnung_${monthKey}_${kunde.firmenId}",
            firmenId = kunde.firmenId,
            firma = kunde.firma,
            datum = monthKey,
            verbrauch = emptyList()
        )).copy(
            firmenId = kunde.firmenId,
            firma = kunde.firma,
            lizenzkey = loaded?.lizenzkey.orEmpty().ifBlank { kunde.lizenzkey },
            datum = monthKey
        )
    }

private fun sysDashboardTile(key: String, label: String, icon: String, value: String): All =
    All(key = key, label = label, icon = icon, bezeichnung = value)

private fun sysDashboardNetzwerkAktiv(row: All): Boolean =
    row.istnetzwerk || row.snapNetzwerkfunktionen

private fun sysDashboardCheckboxText(value: Boolean): String =
    if (value) "\u2705" else "\u2b1c"

private fun sysDashboardCrArtLabel(cr: All): String = when {
    cr.crNeuesModul -> "Neues Modul"
    cr.crAenderungAktivModul.isNotBlank() -> "Änderung: ${cr.crAenderungAktivModul}"
    else -> "Änderung"
}

private fun sysDashboardParseBetrag(value: String): Double {
    val normalized = value.trim()
        .replace("EUR", "", ignoreCase = true)
        .replace("€", "")
        .replace(".", "")
        .replace(",", ".")
        .trim()
    return normalized.toDoubleOrNull() ?: 0.0
}

private fun sysDashboardNetto(container: All): Double =
    sysDashboardParseBetrag(container.gesamtbetragEuro)

private fun sysDashboardMwst(container: All): Double =
    sysDashboardNetto(container) * SYS_DASHBOARD_MWST_SATZ

private fun sysDashboardBrutto(container: All): Double =
    sysDashboardNetto(container) + sysDashboardMwst(container)

private fun sysDashboardEuroText(value: Double): String {
    val formatted = js("new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format")(value) as String
    return "$formatted €"
}

private fun currentSysDashboardMonthKey(): String {
    val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
    return "${now.year}_${now.monthNumber.toString().padStart(2, '0')}"
}
